import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  Skeleton,
  Divider,
} from "@mui/material";
import {
  ScheduleOutlined as TimeIcon,
  PlaceOutlined as PlaceIcon,
  GroupsOutlined as GroupIcon,
} from "@mui/icons-material";
import TutorListPage from "./TutorListPage";
import api from "../../api";

/**
 * When this tutor teaches, and what the course timetable holds besides.
 *
 * Two different questions, and this page used to answer only the second one. `tutor/timeslots` is
 * course-level and tutor-agnostic — every slot of every course the tutor is attached to — so a
 * tutor looking for their own classes had to know which rows were theirs. "My teaching schedule"
 * is the answer: the subjects they hold in a group, each with the weekly slot an admin pinned it to
 * on that group's Staff tab. A subject with no slot is absent rather than shown blank, because an
 * untimetabled subject is a normal state, not a missing row.
 *
 * The full course listing stays below, unchanged — it is still the only place to see the timetable
 * a tutor's colleagues work to.
 */

const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const transform = (row) => ({
  id: row.id,
  course: row.courses?.name ?? "-",
  class_name: row.class_name ?? "-",
  location: row.locations?.name ?? "-",
  week_day: row.weekDays?.name ?? "-",
  start_time: row.start_time ?? "-",
  end_time: row.end_time ?? "-",
});

function ScheduleRow({ entry }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      alignItems={{ sm: "center" }}
      sx={{ py: 0.85, px: 1, borderRadius: 1, "&:hover": { bgcolor: "#f8fafc" } }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 130 }}>
        <TimeIcon sx={{ fontSize: 16, color: "#0277bd" }} />
        <Typography variant="body2" fontWeight={700}>
          {entry.start_time}–{entry.end_time}
        </Typography>
      </Stack>

      <Chip
        size="small"
        label={entry.subject_name}
        sx={{ bgcolor: "#eef2ff", color: "#4338ca", fontWeight: 700 }}
      />

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
        <GroupIcon sx={{ fontSize: 15, color: "#64748b" }} />
        <Typography variant="body2" color="text.secondary" noWrap>
          {entry.group_name}
        </Typography>
      </Stack>

      {entry.location_name && (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <PlaceIcon sx={{ fontSize: 15, color: "#64748b" }} />
          <Typography variant="caption" color="text.secondary">
            {entry.location_name}
          </Typography>
        </Stack>
      )}

      {/* Group delivery stops at the plan end date, so a finished group's slot is history. */}
      {!entry.is_active && <Chip size="small" variant="outlined" label="Group ended" />}
    </Stack>
  );
}

function MySchedule() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api
      .get("tutor/my-schedule")
      .then((res) => {
        if (cancelled) return;
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.message || "Could not load your schedule.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Active groups first: a slot on a group that has finished is history, not this week's plan.
  const byDay = useMemo(() => {
    const active = rows.filter((r) => r.is_active);
    const ended = rows.filter((r) => !r.is_active);
    return DAY_ORDER.map((day) => ({
      day,
      entries: [...active, ...ended].filter((r) => r.weekday_name === day),
    })).filter((d) => d.entries.length > 0);
  }, [rows]);

  const weeklyCount = rows.filter((r) => r.is_active).length;

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: "1px solid #eef2f7" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>
          My teaching schedule
        </Typography>
        {weeklyCount > 0 && (
          <Chip
            size="small"
            label={`${weeklyCount} class${weeklyCount === 1 ? "" : "es"} a week`}
            sx={{ bgcolor: "#ecfdf5", color: "#047857", fontWeight: 700 }}
          />
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The subjects you cover in each group, at the times set for them.
      </Typography>

      {error && <Alert severity="error">{error}</Alert>}

      {loading ? (
        <Skeleton variant="rounded" height={140} />
      ) : byDay.length === 0 ? (
        <Alert severity="info">
          No times are set for your subjects yet. An admin sets these on the group's Staff tab — until
          then you can still schedule each class at whatever time suits.
        </Alert>
      ) : (
        <Stack divider={<Divider flexItem />} spacing={1}>
          {byDay.map(({ day, entries }) => (
            <Box key={day} sx={{ pt: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#334155", mb: 0.25 }}>
                {day}
              </Typography>
              {entries.map((e) => (
                <ScheduleRow key={`${e.course_plan_id}-${e.subject_id}-${e.slot_id}`} entry={e} />
              ))}
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}

export default function TutorTimeslots() {
  return (
    <Box>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 } }}>
        <MySchedule />
      </Box>
      {/* The whole timetable of every course you are attached to — your colleagues' classes too. */}
      <TutorListPage
        title="All course timeslots"
        endpoint="tutor/timeslots"
        columns={{
          id: "ID",
          course: "Course",
          class_name: "Class",
          location: "Location",
          week_day: "Day",
          start_time: "Start",
          end_time: "End",
        }}
        transformRow={transform}
      />
    </Box>
  );
}
